import { useEffect, useState } from 'react';
import { getDiscordSdk } from './sdk-client';

/**
 * Wire shape returned by the SDK's `getInstanceConnectedParticipants` command
 * and `ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE` event. Snake_case is Discord's,
 * pinned here so the normalization boundary in `normalize()` is the *only*
 * place we touch raw SDK shapes — every consumer downstream sees the clean
 * `InstanceParticipant` form.
 */
interface RawSdkParticipant {
  readonly id: string;
  readonly username: string;
  readonly discriminator: string;
  readonly bot: boolean;
  readonly avatar?: string | null;
  readonly global_name?: string | null;
  readonly nickname?: string;
}

/**
 * Normalized participant rendered by `<InstanceParticipantsHeader>`. The hook
 * resolves Discord's display-name hierarchy (nickname > global_name >
 * username) and the avatar URL (custom asset or default-avatar fallback)
 * once at the boundary so downstream rendering deals in plain strings.
 */
export interface InstanceParticipant {
  readonly id: string;
  readonly displayName: string;
  readonly avatarUrl: string;
}

const PARTICIPANTS_UPDATE_EVENT = 'ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE' as const;

/**
 * Drives the live co-presence header at the top of authorized Activity views
 * (#4103).
 *
 * Issues a one-shot `getInstanceConnectedParticipants` on mount, then keeps
 * the list in sync via `ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE` subscriptions.
 * The SDK is a singleton (`sdk-client.ts`) — only `<AuthorizedView>` calls
 * this hook in practice, so the singleton's subscriber list stays small.
 *
 * Bots (notably the Discord activity SDK proxy itself) are filtered at the
 * normalization step: the header is for *people* you're co-present with,
 * not the infrastructure that makes the Activity work.
 *
 * The effect deliberately doesn't gate the initial fetch on the subscribe
 * call resolving — the one-shot lands first so the strip renders without
 * waiting for the event channel's IPC handshake, and the subscription
 * keeps it fresh thereafter. A `cancelled` flag prevents a late-arriving
 * initial response from clobbering an unmounted state.
 */
export function useInstanceParticipants(): readonly InstanceParticipant[] {
  const [participants, setParticipants] = useState<readonly InstanceParticipant[]>([]);

  useEffect(() => {
    const sdk = getDiscordSdk();
    const handleUpdate = (data: { readonly participants: readonly RawSdkParticipant[] }) => {
      setParticipants(normalize(data.participants));
    };

    let cancelled = false;

    void (async () => {
      const initial = await sdk.commands.getInstanceConnectedParticipants();
      if (cancelled) return;
      setParticipants(normalize(initial.participants as readonly RawSdkParticipant[]));
      await sdk.subscribe(PARTICIPANTS_UPDATE_EVENT, handleUpdate);
    })();

    return () => {
      cancelled = true;
      void sdk.unsubscribe(PARTICIPANTS_UPDATE_EVENT, handleUpdate);
    };
  }, []);

  return participants;
}

function normalize(raw: readonly RawSdkParticipant[]): readonly InstanceParticipant[] {
  const result: InstanceParticipant[] = [];
  for (const p of raw) {
    if (p.bot) continue;
    result.push({
      id: p.id,
      displayName: resolveDisplayName(p),
      avatarUrl: resolveAvatarUrl(p),
    });
  }
  return result;
}

function resolveDisplayName(p: RawSdkParticipant): string {
  if (p.nickname) return p.nickname;
  if (p.global_name) return p.global_name;
  return p.username;
}

/**
 * Discord CDN URLs:
 *  - Custom avatar set: `cdn.discordapp.com/avatars/{userId}/{hash}.png`
 *  - No custom avatar:  `cdn.discordapp.com/embed/avatars/{idx}.png`,
 *    where `idx = (BigInt(userId) >> 22n) % 6n` per the username-migration
 *    formula (six default avatars in the new system; the legacy
 *    discriminator-based formula is dead).
 *    https://discord.com/developers/docs/reference#image-formatting
 */
function resolveAvatarUrl(p: RawSdkParticipant): string {
  if (p.avatar) {
    return `https://cdn.discordapp.com/avatars/${p.id}/${p.avatar}.png?size=64`;
  }
  const index = Number((BigInt(p.id) >> 22n) % 6n);
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
}
